import React from 'react';
import { NumberStepper } from '../../../components/technician/ui/NumberStepper';
import { KEYPAD_ADDRESS_MODE, KEYPAD_FAMILY_SPECS, isAddressInRange } from '../catalog/family';
import type { KeypadFamilyKey } from '../catalog/family';
import { KEYPAD_LIMITS } from '../catalog/limits';

export interface AddressFieldProps {
  /** The family whose address rules apply. */
  readonly family: KeypadFamilyKey;
  /** The address the panel answers on now, or null when it has none. */
  readonly address: number | null;
  /** Addresses the other panels in this group already answer on. */
  readonly taken: readonly number[];
  readonly onChange: (address: number) => void;
}

/** Next address nothing in this group answers on, or null when all are used. */
const nextFree = (taken: readonly number[]): number | null => {
  for (let address = KEYPAD_LIMITS.ADDRESS_MIN; address <= KEYPAD_LIMITS.ADDRESS_MAX; address += 1) {
    if (!taken.includes(address)) return address;
  }
  return null;
};

/**
 * The address a panel answers on.
 *
 * A corridor panel and a keycard reader are placed once per RCU at the address
 * the firmware expects, so they show it rather than offering a choice. A panel
 * with a selectable address gets a stepper, what else is in use, and one tap to
 * take the next free number.
 */
export const AddressField: React.FC<AddressFieldProps> = ({
  family,
  address,
  taken,
  onChange,
}) => {
  const spec = KEYPAD_FAMILY_SPECS[family];
  const free = nextFree(taken);

  if (spec.addressMode === KEYPAD_ADDRESS_MODE.FIXED) {
    const groups = spec.fixedSelectors
      .map((selector) => `group ${selector.group} address ${selector.address}`)
      .filter((entry, index, all) => all.indexOf(entry) === index);
    return (
      <div>
        <span className="mb-1.5 block text-sm font-medium text-[#333333]">Address</span>
        <p className="rounded-xl bg-[#F5F5F5] px-4 py-3 text-sm text-[#666666]">
          {spec.label} keys answer on {groups.join(' and ')} — fixed by the firmware, so there is
          nothing to choose.
        </p>
      </div>
    );
  }

  const current = address;
  const invalid = current !== null && !isAddressInRange(current);

  return (
    <div>
      <NumberStepper
        label="Address"
        value={current ?? KEYPAD_LIMITS.ADDRESS_MIN}
        min={KEYPAD_LIMITS.ADDRESS_MIN}
        max={KEYPAD_LIMITS.ADDRESS_MAX}
        onChange={onChange}
        suffix=""
        hint={
          invalid
            ? `Address ${current} is outside the range a panel can answer on.`
            : current === null
              ? 'No address yet. Give it the number the fitter set on the panel.'
              : taken.length === 0
                ? 'Nothing else in this group is using an address.'
                : `In use: ${[...taken].sort((left, right) => left - right).join(', ')}.`
        }
      />
      {free !== null && current !== free && (
        <button
          type="button"
          onClick={() => onChange(free)}
          className="mt-2 text-sm font-medium text-[#D4A054] hover:underline"
        >
          Use the next free address ({free})
        </button>
      )}
    </div>
  );
};

export default AddressField;
